import { Box, Button, Typography, alpha, type SxProps, type Theme } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { ReactNode } from "react";

interface HoldStripProps {
  /** One line, bold: what is held and why, e.g. "On hold: deductible review open". */
  title: string;
  /** The reason in one muted line under the title. */
  reason: ReactNode;
  /** One quiet action on the right; omit for roles that can only read the hold. */
  action?: { label: string; onClick: () => void; testId?: string };
  testId?: string;
  sx?: SxProps<Theme>;
}

/**
 * The in-card hold strip shared by the claim and claimant pages (#7583, #7659):
 * warning tint on the strip only, a dot in place of an icon, the title, the
 * reason in one muted line and one quiet text action with a chevron. The hold
 * stands where the figures or actions it blocks are, instead of a page-wide Alert.
 */
export const HoldStrip = ({ title, reason, action, testId, sx }: HoldStripProps) => (
  <Box
    role="status"
    data-testid={testId}
    sx={[
      (t) => ({
        bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.18 : 0.12),
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
      </Box>
      {action ? (
        <Button
          size="small"
          variant="text"
          color="inherit"
          endIcon={<ChevronRightIcon />}
          onClick={action.onClick}
          data-testid={action.testId}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", whiteSpace: "nowrap", px: 0.75 }}
        >
          {action.label}
        </Button>
      ) : null}
    </Box>
    <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>{reason}</Typography>
  </Box>
);
